import { dataTypes, QuantityInput } from 'cat-ecommerce-alloy';
import AlternativePartsTableMobileView from './AlternativePartsMobileView';
import styles from './styles.modules.scss';
import { ALTERNATIVE_TYPES, getAltParts } from '@app/store/checkout/utils';
import { cartItemPropType } from './declarations.propTypes';
import RemanufacturedPartMessage from '@app/components/common/RemanufacturedPartMessage';
import UnitPriceTableHeader from '../common/UnitPriceTableHeader';
import UnitPriceTableCell from '../common/UnitPriceTableCell';
import { getAvailability } from '../common/commonUtils';
import TooltipDisclaimer from '@app/components/common/Availability/TooltipDisclaimer';
import { useTranslation } from 'react-i18next';
import { PART_TYPE } from '../constants';

const displayOnDesktopOnly = 'd-none d-lg-table-cell';
const displayOnMobileOnly = 'd-lg-none';

export const ALTERNATIVES = 'alternatives';
export const ORIGINAL = 'original';

export const ItemDescription = ({ item }) => {
  const isRemanPart = item.partLevelFlags.isReman;
  return (
    <>
      <span
        className={`${displayOnDesktopOnly} u-color--ExtraDarkGray u-fs--14`}
      >
        {`${item.partNumber}: ${item.partName}`}
      </span>
      {isRemanPart && <RemanufacturedPartMessage />}
    </>
  );
};
ItemDescription.propTypes = {
  item: cartItemPropType
};

export const getPartSchema = (
  currency,
  t,
  isQuantityMergeEnabled,
  isOriginal,
  isDeliveryEnabled,
  isPickUpEnabled
) => [
  {
    id: 'sequence',
    label: '',
    dataType: dataTypes.ALPHANUMERIC,
    className: `${displayOnDesktopOnly} ${
      isOriginal ? 'u-color--ExtraDarkGray u-fs--14' : 'border-bottom-0'
    }`,
    align: 'end',
    width: '2%',
    padding: isOriginal ? '0.75rem' : '1.125rem'
  },
  {
    id: 'quantity',
    label: (
      <span className="u-color--DarkGray u-fs--14 text-univers--bold ps-2">
        {t('QUANTITY1')}
      </span>
    ),
    dataType: dataTypes.ALPHANUMERIC,
    className: `${displayOnDesktopOnly} u-color--ExtraDarkGray u-fs--14`,
    width: '10%',
    padding: '0px 0px 0px 0.5rem'
  },
  {
    id: 'item',
    label: (
      <span className="u-color--DarkGray u-fs--14 text-univers--bold">
        {t('ITEM')}
      </span>
    ),
    className: `${displayOnDesktopOnly} u-color--ExtraDarkGray u-fs--14`,
    width: '30%',
    padding: '0.75rem'
  },
  {
    id: 'type',
    label: isOriginal ? (
      ''
    ) : (
      <span className="u-color--DarkGray u-fs--14 text-univers--bold">
        {`${t('TYPE')}`}
      </span>
    ),
    width: '18%',
    className: `${displayOnDesktopOnly} u-color--ExtraDarkGray u-fs--14`,
    padding: '0.75rem'
  },
  {
    id: 'availability',
    label: (
      <span>
        <span className="u-color--DarkGray u-fs--14 text-univers--bold">
          {t('SHOPCART_AVAILABILITY')}
          {!isQuantityMergeEnabled && <sup>&dagger;</sup>}
          <TooltipDisclaimer />
        </span>
      </span>
    ),
    width: '25%',
    className: `${displayOnDesktopOnly} u-color--ExtraDarkGray u-fs--14`,
    padding: '0.75rem 0.5rem'
  },
  {
    id: 'unitPrice',
    label: <UnitPriceTableHeader currency={currency} />,
    dataType: dataTypes.NUMBER,
    className: `${displayOnDesktopOnly} u-color--ExtraDarkGray u-fs--14`,
    width: '15%'
  },
  ...(isOriginal
    ? [
        {
          id: 'mobileCell',
          className: displayOnMobileOnly,
          width: '90%'
        }
      ]
    : [])
];

export const getQuantitiesError = (quantities, t) => {
  const invalidQuantityError = t('SKULIST_INVALID_QUANTITY');
  const error = Object.keys(quantities).some(key => quantities[key] > 0)
    ? null
    : invalidQuantityError;
  return error;
};
export const getType = alternativeType => {
  if (alternativeType === ALTERNATIVE_TYPES.CLASSIC) {
    return 'CAT_QO_GENERAL_DUTY';
  }
  if (alternativeType === ALTERNATIVE_TYPES.REMANUFACTURED) {
    return 'CAT_QO_REMANUFACTURED';
  }
  return 'CAT_QO_ALTERNATE';
};

const getTextByPartType = {
  [PART_TYPE.REMAN]: 'CAT_QO_REMAN',
  [PART_TYPE.CLASSIC]: 'CAT_QO_GENERAL_DUTY',
  [PART_TYPE.ALTERNATE]: 'CAT_QO_OTHER_CAT_PART',
  [PART_TYPE.DEALERREMAN]: 'CAT_QO_DEALER_REMANUFACTURED',
  [PART_TYPE.DEALERALTERNATE]: 'CAT_QO_DEALER_OTHER'
};

export const getPartTypeValue = partType => {
  return getTextByPartType[partType];
};

export const AlternativeType = ({ partType }) => {
  const [t] = useTranslation();
  return (
    <span className={`${displayOnDesktopOnly} u-color--ExtraDarkGray u-fs--14`}>
      {t(getPartTypeValue(partType))}
    </span>
  );
};
export const getCustomRenderers = (
  currency,
  alternateParts,
  isStoreAvailabilityEnabled,
  sourcingToggle,
  hoseAvailabilityLabel,
  quantities,
  setQuantity,
  showQuantityError,
  t
) => ({
  quantity: item => (
    <QuantityInput
      className={styles['quantity-input']}
      value={quantities[item.referenceId]?.toString() ?? '0'}
      /**
       * Setting error instead of validator because we need to consider all QuantityInputs
       * in the tables and the validator only fires on changes of current QuantityInput
       * Note: must have a setter in order for error state to propogate to QuantityInput component
       */
      error={
        getQuantitiesError(quantities, t) ||
        showQuantityError(item, quantities[item.referenceId])
      }
      setError={() => null}
      setValue={value => setQuantity(item.referenceId, parseInt(value) ?? 0)}
    />
  ),
  item: item => <ItemDescription item={item} />,
  type: item => <AlternativeType partType={item.partType} />,
  availability: item =>
    getAvailability(
      item,
      isStoreAvailabilityEnabled,
      hoseAvailabilityLabel,
      sourcingToggle
    ),
  unitPrice: item => (
    <UnitPriceTableCell
      unitPrice={item?.unitPrice}
      coreCharges={item.price?.formatted?.coreCharges}
      showCoreCharges={!!item.price?.unformatted?.coreCharges}
      currency={currency}
    />
  ),
  mobileCell: item =>
    item?.typeElement === ORIGINAL && (
      <AlternativePartsTableMobileView
        alternateParts={alternateParts}
        availabilityTypeToggle={sourcingToggle}
        item={item}
        currency={currency}
        quantities={quantities}
        setQuantity={setQuantity}
        showQuantityError={showQuantityError}
      />
    )
});

/**
 * Returns a dictionary with items part numbers as keys and their quantities as values
 * @param {Object} items
 * @returns {Object}
 */
export const getQuantities = items =>
  Object.keys(items).reduce(
    (obj, itemKey) => ({
      ...obj,
      [itemKey]: { [itemKey]: parseInt(items[itemKey].requestedQuantity) || 0 }
    }),
    {}
  );

/**
 * Returns a new items object with the ones that have quantity, to update the cart
 * @param {Object} items
 * @param {Object} quantities
 * @returns {Object}
 */
export const getItemsToSend = (quantities, items) =>
  Object.keys(quantities).reduce((itemsToSend, key) => {
    let newItemsToSend = { ...itemsToSend };
    const requestedQuantity = quantities[key][key];
    if (requestedQuantity) {
      newItemsToSend = {
        ...newItemsToSend,
        [key]: { ...items[key], requestedQuantity }
      };
    }
    return {
      ...newItemsToSend,
      ...Object.keys(quantities[key])
        .filter(altKey => altKey !== key && !!quantities[key][altKey])
        .reduce((altItemsToSend, altKey) => {
          const originalItem = items[key];
          const altParts = getAltParts(originalItem);
          const item = altParts.find(p => p.referenceId === altKey);
          return {
            ...altItemsToSend,
            [altKey]: {
              ...item,
              lineItemNote: originalItem.lineItemNote,
              customerItemNumber: originalItem.customerItemNumber,
              customerPartNumber: originalItem.customerPartNumber,
              assetId: originalItem.assetId,
              requestedQuantity: quantities[key][altKey]
            }
          };
        }, {})
    };
  }, {});

/**
 * Returns a new items object with only original parts that have quantity, to update the cart
 * @param {Object} items
 * @param {Object} quantities
 * @returns {Object}
 */
export const getOriginalItemsToSend = (quantities, items) =>
  Object.keys(quantities).reduce((itemsToSend, key) => {
    let newItemsToSend = { ...itemsToSend };
    const requestedQuantity = quantities[key][key];
    if (requestedQuantity) {
      newItemsToSend = {
        ...newItemsToSend,
        [key]: { ...items[key], requestedQuantity }
      };
    }
    return newItemsToSend;
  }, {});
